import {CHAT_WS_DISABLED, fetchChatWsTicket} from "@/api/chatApi";

export type TChatSocketStatus = "idle" | "connecting" | "open" | "closed";
export type TChatSocketMessage = {type: string; [key: string]: unknown};
type TMessageListener = (message: TChatSocketMessage) => void;
type TStatusListener = (status: TChatSocketStatus) => void;

export type TChatSocket = {
  getStatus: () => TChatSocketStatus;
  isOpen: () => boolean;
  onMessage: (listener: TMessageListener) => () => void;
  onStatus: (listener: TStatusListener) => () => void;
  request: (payload: Record<string, unknown>, timeoutMs?: number) => Promise<TChatSocketMessage>;
  send: (payload: Record<string, unknown>) => boolean;
  start: () => void;
  stop: () => void;
};

// Rejection reason when the socket wasn't open at send time — the only case a
// caller may safely retry over REST (nothing was transmitted).
export const CHAT_WS_NOT_OPEN = "chat_ws_not_open";
const REQUEST_TIMEOUT_MS = 10_000;

const HEARTBEAT_INTERVAL_MS = 25_000; // stays under the 60s proxy/ALB idle cutoff
const RECONNECT_BASE_MS = 1_000;
const RECONNECT_MAX_MS = 30_000;
const CHAT_WS_PATH = "/ws/chat/";

// The WebSocket needs a host that can perform the HTTP upgrade. The SPA origin
// can't when it fronts the API through an Amplify rewrite (HTTP-only proxy), so
// VITE_APP_WS_BASE_URL points the socket straight at the API host (e.g.
// wss://api.dev.core.refluenced.ch). It accepts http(s) or ws(s). Falls back to
// the REST base / current origin when unset.
const wsBaseUrl = (): string =>
  (import.meta.env.VITE_APP_WS_BASE_URL || import.meta.env.VITE_APP_API_BASE_URL || window.location.origin)
    .replace(/^http/, "ws")
    // Strip any trailing slash so `${base}${CHAT_WS_PATH}` can't produce `//ws/chat/`.
    .replace(/\/+$/, "");

const createChatSocket = (): TChatSocket => {
  let ws: WebSocket | null = null;
  let status: TChatSocketStatus = "idle";
  let shouldRun = false;
  let connecting = false;
  // Bumped by stop() (and each connect()); lets an in-flight connect() that was
  // superseded while awaiting its ticket bail out instead of opening a stray socket.
  let connectToken = 0;
  let reconnectAttempts = 0;
  let heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  const messageListeners = new Set<TMessageListener>();
  const statusListeners = new Set<TStatusListener>();

  const setStatus = (next: TChatSocketStatus): void => {
    if (status === next) return;
    status = next;
    statusListeners.forEach(listener => listener(next));
  };

  const send = (payload: Record<string, unknown>): boolean => {
    if (ws?.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify(payload));
      return true;
    }
    return false;
  };

  const clearHeartbeat = (): void => {
    if (heartbeatTimer) {
      clearInterval(heartbeatTimer);
      heartbeatTimer = null;
    }
  };

  const startHeartbeat = (): void => {
    clearHeartbeat();
    heartbeatTimer = setInterval(() => send({type: "ping"}), HEARTBEAT_INTERVAL_MS);
  };

  const clearReconnect = (): void => {
    if (reconnectTimer) {
      clearTimeout(reconnectTimer);
      reconnectTimer = null;
    }
  };

  const stop = (): void => {
    shouldRun = false;
    // Reset connecting (an in-flight connect's onopen may be nulled below and
    // never run) and invalidate that attempt so it can't open a stray socket.
    connecting = false;
    connectToken += 1;
    clearReconnect();
    clearHeartbeat();
    reconnectAttempts = 0;
    if (ws) {
      // Drop handlers first so this normal-closure close() doesn't reconnect.
      ws.onopen = ws.onmessage = ws.onclose = ws.onerror = null;
      ws.close(1000);
      ws = null;
    }
    setStatus("idle");
  };

  const connect = async (): Promise<void> => {
    if (connecting || !shouldRun) return;
    if (ws && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING)) return;
    connecting = true;
    const token = ++connectToken;
    setStatus("connecting");

    let ticket: string;
    try {
      ticket = (await fetchChatWsTicket()).ticket;
    } catch (error) {
      // Only the still-current attempt owns `connecting` / reconnect scheduling.
      if (token !== connectToken) return;
      connecting = false;
      if (error instanceof Error && error.message === CHAT_WS_DISABLED) {
        // Kill switch is on server-side — stop and stay on REST instead of
        // hammering the ticket endpoint on the reconnect backoff.
        stop();
        return;
      }
      scheduleReconnect();
      return;
    }
    // Superseded by stop() (or a newer connect()) while awaiting the ticket —
    // bail out without opening a socket.
    if (token !== connectToken || !shouldRun) {
      if (token === connectToken) connecting = false;
      return;
    }

    try {
      ws = new WebSocket(`${wsBaseUrl()}${CHAT_WS_PATH}?ticket=${encodeURIComponent(ticket)}`);
      ws.onopen = () => {
        connecting = false;
        reconnectAttempts = 0;
        setStatus("open");
        startHeartbeat();
      };
      ws.onmessage = handleMessage;
      ws.onclose = handleClose;
      ws.onerror = () => {
        // A close event always follows; reconnection is handled there.
      };
    } catch {
      connecting = false;
      scheduleReconnect();
    }
  };

  const scheduleReconnect = (): void => {
    if (!shouldRun || reconnectTimer) return;
    const backoff = Math.min(RECONNECT_BASE_MS * 2 ** reconnectAttempts, RECONNECT_MAX_MS);
    const jitter = Math.random() * RECONNECT_BASE_MS;
    reconnectAttempts += 1;
    reconnectTimer = setTimeout(() => {
      reconnectTimer = null;
      void connect();
    }, backoff + jitter);
  };

  const handleMessage = (event: MessageEvent): void => {
    let message: TChatSocketMessage;
    try {
      message = JSON.parse(event.data);
    } catch {
      return;
    }
    if (!message || typeof message.type !== "string") return;
    if (message.type === "pong") return;
    messageListeners.forEach(listener => listener(message));
  };

  const handleClose = (): void => {
    clearHeartbeat();
    ws = null;
    connecting = false;
    if (!shouldRun) return;
    setStatus("closed");
    scheduleReconnect();
  };

  const addMessageListener = (listener: TMessageListener): (() => void) => {
    messageListeners.add(listener);
    return () => messageListeners.delete(listener);
  };

  // Correlate a write with its server ack by a per-request client_id. Resolves
  // with the ack frame, rejects on an error frame, on timeout, or (CHAT_WS_NOT_OPEN)
  // if the socket wasn't open — the caller decides whether to retry over REST.
  const request = (payload: Record<string, unknown>, timeoutMs = REQUEST_TIMEOUT_MS): Promise<TChatSocketMessage> =>
    new Promise((resolve, reject) => {
      const clientId = crypto.randomUUID();
      let unsubscribe = (): void => {};
      const settle = (finish: () => void): void => {
        clearTimeout(timer);
        unsubscribe();
        finish();
      };
      const timer = setTimeout(() => settle(() => reject(new Error("chat_ws_timeout"))), timeoutMs);
      unsubscribe = addMessageListener(message => {
        if (message.client_id !== clientId) return;
        if (message.type === "message.ack") {
          settle(() => resolve(message));
        } else if (message.type === "error") {
          settle(() => reject(new Error(typeof message.detail === "string" ? message.detail : "chat_ws_error")));
        }
      });
      if (!send({...payload, client_id: clientId})) settle(() => reject(new Error(CHAT_WS_NOT_OPEN)));
    });

  return {
    getStatus: () => status,
    isOpen: () => ws?.readyState === WebSocket.OPEN,
    onMessage: addMessageListener,
    request,
    onStatus: listener => {
      statusListeners.add(listener);
      return () => statusListeners.delete(listener);
    },
    send,
    start: () => {
      if (shouldRun) return;
      shouldRun = true;
      void connect();
    },
    stop,
  };
};

export const chatSocket = createChatSocket();
